import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest, getQueryFn, queryClient } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { FolderKanban, Plus, Pencil } from "lucide-react";

interface ProjectListItem {
  id: string;
  clientId: string;
  contractId: string | null;
  budgetId: string | null;
  name: string;
  description: string | null;
  status: "planned" | "active" | "blocked" | "completed" | "archived";
  ownerUserId: string;
  portalVisible: boolean;
  startDate: string | null;
  targetEndDate: string | null;
  clientName: string;
  accountManagerUserId: string | null;
}

interface ProjectOptionsResponse {
  staff: Array<{ id: string; name: string; email: string; role: string }>;
  clients: Array<{ id: string; name: string; accountManagerUserId: string | null }>;
  contracts: Array<{ id: string; clientId: string; title: string | null; status: string }>;
  budgets: Array<{ id: string; contractId: string; name: string; status: string }>;
  serviceItems: Array<{ id: string; budgetId: string; name: string; requiresTask: boolean; isActive: boolean }>;
}

interface ProjectFormState {
  clientId: string;
  contractId: string | null;
  budgetId: string | null;
  name: string;
  description: string;
  status: ProjectListItem["status"];
  ownerUserId: string;
  portalVisible: boolean;
  startDate: string;
  targetEndDate: string;
}

const emptyForm: ProjectFormState = {
  clientId: "",
  contractId: null,
  budgetId: null,
  name: "",
  description: "",
  status: "planned",
  ownerUserId: "",
  portalVisible: false,
  startDate: "",
  targetEndDate: "",
};

function toDateInput(value: string | null) {
  return value ? value.slice(0, 10) : "";
}

export default function ProjectsPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [search, setSearch] = useState("");
  const [accountManagerId, setAccountManagerId] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<ProjectListItem | null>(null);
  const [form, setForm] = useState<ProjectFormState>(emptyForm);

  const querySuffix = new URLSearchParams(
    Object.entries({
      ...(search ? { search } : {}),
      ...(accountManagerId !== "all" ? { accountManagerId } : {}),
    }),
  ).toString();

  const { data: projects, isLoading } = useQuery<ProjectListItem[]>({
    queryKey: [`/api/projects${querySuffix ? `?${querySuffix}` : ""}`],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: options } = useQuery<ProjectOptionsResponse>({
    queryKey: ["/api/projects/meta/options"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const staffMap = useMemo(
    () => new Map(options?.staff.map((member) => [member.id, member.name]) ?? []),
    [options],
  );

  const contractsForClient = options?.contracts.filter(
    (contract) => contract.clientId === form.clientId,
  ) ?? [];
  const budgetsForContract = options?.budgets.filter(
    (budget) => budget.contractId === form.contractId,
  ) ?? [];

  const createMutation = useMutation({
    mutationFn: async (payload: ProjectFormState) => {
      const response = await apiRequest("POST", "/api/projects", {
        ...payload,
        contractId: payload.contractId,
        budgetId: payload.budgetId,
        description: payload.description || null,
        startDate: payload.startDate || null,
        targetEndDate: payload.targetEndDate || null,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/projects/meta/options"] });
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          query.queryKey[0].startsWith("/api/projects"),
      });
      setDialogOpen(false);
      setEditingProject(null);
      setForm(emptyForm);
      toast({ title: "Project created" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create project",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: ProjectFormState) => {
      const response = await apiRequest("PATCH", `/api/projects/${editingProject?.id}`, {
        ...payload,
        contractId: payload.contractId,
        budgetId: payload.budgetId,
        description: payload.description || null,
        startDate: payload.startDate || null,
        targetEndDate: payload.targetEndDate || null,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          query.queryKey[0].startsWith("/api/projects"),
      });
      setDialogOpen(false);
      setEditingProject(null);
      setForm(emptyForm);
      toast({ title: "Project updated" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update project",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const columns: ColumnDef<ProjectListItem>[] = [
    {
      key: "name",
      header: "Project",
      cell: (project) => (
        <div className="space-y-1">
          <p className="font-medium">{project.name}</p>
          <p className="text-xs text-muted-foreground">{project.clientName}</p>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (project) => <Badge variant="outline" className="capitalize">{project.status.replace("_", " ")}</Badge>,
    },
    {
      key: "owner",
      header: "Owner",
      cell: (project) => staffMap.get(project.ownerUserId) ?? "Unknown",
    },
    {
      key: "portal",
      header: "Portal",
      cell: (project) => (
        <Badge variant={project.portalVisible ? "default" : "secondary"}>
          {project.portalVisible ? "Visible" : "Hidden"}
        </Badge>
      ),
    },
    {
      key: "dates",
      header: "Dates",
      cell: (project) => (
        <div className="text-xs text-muted-foreground">
          <p>Start: {project.startDate ? toDateInput(project.startDate) : "Not set"}</p>
          <p>End: {project.targetEndDate ? toDateInput(project.targetEndDate) : "Not set"}</p>
        </div>
      ),
    },
    {
      key: "actions",
      header: "",
      cell: (project) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(event) => {
            event.stopPropagation();
            setEditingProject(project);
            setForm({
              clientId: project.clientId,
              contractId: project.contractId,
              budgetId: project.budgetId,
              name: project.name,
              description: project.description ?? "",
              status: project.status,
              ownerUserId: project.ownerUserId,
              portalVisible: project.portalVisible,
              startDate: toDateInput(project.startDate),
              targetEndDate: toDateInput(project.targetEndDate),
            });
            setDialogOpen(true);
          }}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      ),
      className: "w-[60px]",
    },
  ];

  const accountManagers = options?.staff.filter((staff) =>
    options.clients.some((client) => client.accountManagerUserId === staff.id),
  ) ?? [];

  function openCreate() {
    setEditingProject(null);
    setForm({
      ...emptyForm,
      ownerUserId: options?.staff[0]?.id ?? "",
    });
    setDialogOpen(true);
  }

  function handleSubmit() {
    if (editingProject) {
      updateMutation.mutate(form);
      return;
    }

    createMutation.mutate(form);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Projects"
        description="Delivery work, board status, and portal-facing visibility."
        action={
          <Button onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            New Project
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={projects}
        isLoading={isLoading}
        rowKey={(project) => project.id}
        onRowClick={(project) => navigate(`/projects/${project.id}`)}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search projects..."
        filterSlot={
          <Select value={accountManagerId} onValueChange={setAccountManagerId}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Filter by account manager" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All account managers</SelectItem>
              {accountManagers.map((manager) => (
                <SelectItem key={manager.id} value={manager.id}>
                  {manager.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
        emptyIcon={FolderKanban}
        emptyTitle="No projects"
        emptyDescription="Create the first delivery project to start tracking scheduled work."
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>{editingProject ? "Edit project" : "Create project"}</DialogTitle>
            <DialogDescription>
              Link the project to the client/commercial model and decide whether it is portal-visible.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="project-name">Project name</Label>
              <Input
                id="project-name"
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="project-description">Description</Label>
              <Textarea
                id="project-description"
                value={form.description}
                onChange={(event) =>
                  setForm((current) => ({ ...current, description: event.target.value }))
                }
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label>Client</Label>
              <Select
                value={form.clientId || undefined}
                onValueChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    clientId: value,
                    contractId: null,
                    budgetId: null,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {options?.clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Owner</Label>
              <Select
                value={form.ownerUserId || undefined}
                onValueChange={(value) => setForm((current) => ({ ...current, ownerUserId: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select owner" />
                </SelectTrigger>
                <SelectContent>
                  {options?.staff.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Contract</Label>
              <Select
                value={form.contractId ?? "none"}
                onValueChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    contractId: value === "none" ? null : value,
                    budgetId: null,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Optional contract" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No linked contract</SelectItem>
                  {contractsForClient.map((contract) => (
                    <SelectItem key={contract.id} value={contract.id}>
                      {contract.title || contract.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Budget</Label>
              <Select
                value={form.budgetId ?? "none"}
                onValueChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    budgetId: value === "none" ? null : value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Optional budget" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No linked budget</SelectItem>
                  {budgetsForContract.map((budget) => (
                    <SelectItem key={budget.id} value={budget.id}>
                      {budget.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={form.status}
                onValueChange={(value: ProjectListItem["status"]) =>
                  setForm((current) => ({ ...current, status: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-start">Start date</Label>
              <Input
                id="project-start"
                type="date"
                value={form.startDate}
                onChange={(event) => setForm((current) => ({ ...current, startDate: event.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-end">Target end</Label>
              <Input
                id="project-end"
                type="date"
                value={form.targetEndDate}
                onChange={(event) =>
                  setForm((current) => ({ ...current, targetEndDate: event.target.value }))
                }
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3 md:col-span-2">
              <div>
                <p className="text-sm font-medium">Portal visibility</p>
                <p className="text-xs text-muted-foreground">
                  Clients can only see projects and tasks when this is enabled.
                </p>
              </div>
              <Switch
                checked={form.portalVisible}
                onCheckedChange={(checked) =>
                  setForm((current) => ({ ...current, portalVisible: checked }))
                }
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !form.clientId ||
                !form.ownerUserId ||
                !form.name.trim() ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {editingProject ? "Save changes" : "Create project"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
